// Cliente de telemetría liviano (fire-and-forget, PHI-safe).
//
// Emite eventos de uso (module_open, turno_generate, censo_paste, exam_view, ...)
// al endpoint batch `POST /api/medical_analysis/activity/`, que SIEMPRE responde
// 200 (fail-open) y sanea el `meta` con un allowlist PHI-safe en el backend.
//
// ⚠️ REGLA DURA PHI: `meta` SOLO puede llevar ids/counts/bools/enums — NUNCA
// nombre de paciente, RUT, texto clínico, contenido de censo, ni nada que
// identifique a una persona o exponga datos de salud. Si dudas si un valor es
// PHI, NO lo mandes. El backend igual filtra, pero la primera línea de defensa
// es no encolarlo.
//
// Diseño fail-silent total: cualquier error en el flush se traga (console.debug).
// La telemetría JAMÁS rompe la app ni bloquea la navegación.
//
// ⚠️ 21-sep-2026 (#752) — LO QUE SE ARREGLÓ ACÁ, y por qué.
// Este módulo perdía eventos por cinco caminos a la vez, todos mudos. Medido en
// producción: un médico generó 35 documentos en dos días hábiles y su cliente no
// emitió UN SOLO evento, mientras los otros médicos de esos días emitían normal.
//   1. Posteaba contra la URL ABSOLUTA del backend y no conocía el fallback
//      same-origin que el resto de la app usa desde el 15-ago. En toda red que
//      bloquea `backend.smardx.com` (hospitales, documentado) la app clínica
//      seguía andando por el proxy y la telemetría moría entera. ESA fue la causa.
//   2. La cola se vaciaba ANTES de enviar: si el envío fallaba, no quedaba de
//      dónde recuperarla.
//   3. Sin token descartaba el lote completo, en silencio.
//   4. `fetch().catch()` solo atrapa fallos de RED: un 401 o un 502 son promesas
//      RESUELTAS, así que se contaban como éxito.
//   5. Nada persistía: un reload, un crash o la recarga del service worker
//      suicida se llevaban la cola.
// Ahora la entrega es AL MENOS UNA VEZ: la cola sobrevive en `localStorage` y
// solo se descarta contra un 2xx. El backend deduplica por (session_id, seq).

import { urlApi, proxyYaComprobado, recordarProxy } from "./api-base";
import { isStandalone } from "./pwa-install";

const ACTIVITY_PATH = "/api/medical_analysis/activity/";

const FLUSH_INTERVAL_MS = 10000; // flush periódico cada 10s si hay cola
const MAX_BATCH = 50; // el backend acepta máx 50 eventos/request

// Techo del buffer retenido. Un médico genera decenas de eventos por hora; 500
// cubre varias horas sin red. Al pasarse se descarta lo MÁS VIEJO y se deja un
// evento `activity_dropped` con el conteo: un descarte contado es un dato, uno
// mudo es exactamente el bug que este archivo vino a arreglar.
const MAX_BUFFER = 500;

// Más viejo que esto no se manda: un evento de hace días ya no sirve para nada
// y su sesión probablemente ni exista.
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

const BACKOFF_BASE_MS = 2000;
const BACKOFF_MAX_MS = 60000;
// Mismo criterio que la sonda de `get-client`: un firewall que descarta en
// silencio no rechaza, hace esperar. Sin techo, el flush queda tomado para
// siempre.
const TIMEOUT_MS = 8000;

// Prefijo del buffer persistido. LLEVA EL DUEÑO en la clave, igual que
// `turno-draft-buffer`: sin eso, los eventos que quedaron sin enviar de un
// médico saldrían con el token del siguiente que entre en ese computador, y
// quedarían atribuidos a él. Es la forma exacta de INC-2026-27 y del cruce del
// buffer de borradores — dos veces el mismo error, no hay tercera.
const PREFIX = "smartdx.activity.v1:";

// session_id generado una vez por carga de la página (NO es PHI: solo agrupa
// eventos de una misma sesión de navegador). Se mantiene en memoria del módulo.
const SESSION_ID = `s_${Date.now().toString(36)}_${Math.random()
  .toString(36)
  .slice(2, 10)}`;

// Cola en memoria. Se vacía en tandas de MAX_BATCH.
let queue = [];
let listenersWired = false;

// Hay un envío en vuelo: evita que dos flushes manden el mismo lote a la vez
// (el timer de 10 s y un `visibilitychange` pueden caer juntos).
let enVuelo = false;
// Cuándo arrancó el envío en vuelo, para poder soltarlo si se cuelga.
let enVueloDesde = 0;
// Fallos consecutivos, para el backoff. Se resetea con el primer 2xx.
let intentosFallidos = 0;
let timerReintento = null;
// Total descartado por tope desde la última entrega. Vive fuera de la cola
// porque el marcador que lo lleva también puede ser descartado.
let descartadosAcumulados = 0;
// Lo que se llevó el lote que está EN VUELO. Se descuenta del acumulado al armar
// el lote, no al confirmarlo: si se descuenta recién en el `.then`, un evento
// nuevo que llegue mientras tanto hace que `asegurarMarcador()` fabrique un
// segundo marcador con el MISMO total, y el huérfano se entrega igual. Medido:
// dos filas `activity_dropped` con count 3 para 3 descartes reales — el
// instrumento de honestidad inflando su propia métrica al doble.
let descartadosEnVuelo = 0;
// Los eventos del lote que está en vuelo. El tope de buffer NO puede tirarlos:
// ya salieron a la red y se van a entregar, así que descartarlos infla el
// contador de pérdida con eventos que no se perdieron. Lo encontró el test del
// marcador, comparando lo encolado contra lo entregado.
let loteEnVuelo = new Set();

// Contador monotónico por carga de página. Va en `meta.seq` y existe por una
// razón concreta: el flush manda hasta 50 eventos juntos, así que decenas caen
// en el MISMO segundo de `created_at` y el backend no puede reconstruir en qué
// orden pasaron. Sin esto el timeline muestra "movió cama 3 → 7" antes de
// "escribió en cama 3", que es justo al revés de lo que hizo el médico.
let seqCounter = 0;

// El backend solo acepta valores string que sean slug en minúscula
// (`^[a-z0-9][a-z0-9_:-]{0,63}$`) — todo lo demás lo descarta en SILENCIO como
// posible PHI. Una cama "A" o "Box 3" caería sin dejar rastro y el timeline
// diría "escribió en cama {bed}" sin cama. Esto normaliza acá, del lado que
// sabe qué es el dato, y deja un valor explícito cuando no se puede.
//
// ⚠️ NO usar esto para nada que venga del paciente: es para identificadores de
// UBICACIÓN (cama, box). El nombre o el RUT no se sanean, no se mandan.
export function sanitizeSlug(value) {
  try {
    return String(value ?? "")
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") // saca tildes: "académicas" -> "academicas"
      .replace(/\s+/g, "_") // espacios → _: "DUDAS ACADEMICAS" → "dudas_academicas"
      .replace(/[^a-z0-9_:-]/g, "")
      .slice(0, 63);
  } catch (_) {
    return "";
  }
}

export function sanitizeBed(value) {
  try {
    const slug = sanitizeSlug(value);
    if (!slug) return "vacia";
    // Un run largo de dígitos lo rechaza el backend por parecer RUT. Una cama
    // real nunca tiene 7+ dígitos, así que si llega algo así es un error de
    // cableado — mejor marcarlo que mandar algo que se descarta solo.
    if (/\d{7,}/.test(slug)) return "invalida";
    return slug.slice(0, 63);
  } catch (_) {
    return "invalida";
  }
}

// Lee el token igual que get-client.js (localStorage "authToken", header
// `Authorization: Token <token>`). Se lee en cada flush para no mandar tokens
// stale.
function getAuthToken() {
  try {
    return localStorage.getItem("authToken");
  } catch (_) {
    return null;
  }
}

// ── Barrera PHI del lado del cliente ────────────────────────────────────────
// Hasta el 21-sep-2026 el `meta` viajaba crudo y era el BACKEND el que filtraba.
// Eso alcanzaba mientras la cola viviera 10 segundos en memoria. Ahora la cola
// se PERSISTE hasta 24 h en el disco del médico, así que un `meta` sin sanear
// sería texto clínico escrito en un computador de turno — un agujero que abrió
// esta misma persistencia.
//
// La regla es la MISMA del backend (`activity.py`, `_STR_VALUE_RE`), no una
// heurística nueva: los strings tienen que ser slugs en minúscula de hasta 64
// caracteres. Números y booleanos pasan. Lo que no, se cae acá y no llega al
// disco. Si el backend afloja o endurece su regla, esto queda más estricto, que
// es el lado seguro.
// ⚠️ Las DOS barreras del backend, en el mismo orden. La primera versión de
// esta función decía "la regla es la MISMA del backend… queda más estricto" y
// era falso en los dos puntos, medido por el revisor:
//   · le agregué `.` a la clase de caracteres, que es justo lo que separa un RUT
//     chileno, así que `12.345.678-9` pasaba entero;
//   · copié solo el slug y no el chequeo de RUT, así que `12345678-9` —que el
//     backend rechaza— también pasaba.
// El identificador más regulado por la Ley 21.719 entraba sin tocar la puerta, y
// ahora eso se escribe al disco del médico por 24 h.
// ⚠️ EL ORDEN ES EL ARREGLO, y esto ya pasó del otro lado de este mismo cable.
//
// La barrera anti-RUT es una corrida larga de dígitos (el criterio de
// `sanitizeBed`: "un run largo de dígitos lo rechaza el backend por parecer
// RUT"). El problema es que un UUID v4 tiene 8 hex al principio y **uno de cada
// seis y medio sale todo en dígitos**. Medido acá sobre 200.000 UUID:
// `\d{7,}` sola descarta el **15,28%**.
//
// El backend cometió exactamente este error en agosto y lo dejó escrito con su
// número (`activity.py`): "la expresión del RUT no está anclada, así que
// cualquier corrida de 8 dígitos adentro del hex la matchea. Medido: el 8,83%
// de los UUID v4 se descartaban solos, en silencio". Su arreglo fue una
// allowlist de ids opacos validados contra el patrón UUID **antes** de las
// heurísticas. Yo copié la mitad que bloquea y no la que protege, y acá pesa
// más: esto corre ANTES del disco, así que el backend ya no puede recuperarlo.
//
// Y `gen_id` es el ancla que ata una edición a la generación que la escribió —
// el corazón de la medición de este mismo item.
// Ids opacos: `gen_id` por nombre, y CUALQUIER key que termine en `_id`. La
// primera versión listaba solo `gen_id`, así que quedaba a merced del próximo
// call site: el día que alguien escriba `tab_id: String(tab.id)` con un id
// largo, la heurística anti-RUT se lo come en silencio y nadie se entera. La
// regla por forma lo vuelve inmune a eso.
// Un id que viaja como STRING solo se acepta si es un UUID.
//
// La versión anterior aceptaba además cualquier corrida de hasta 18 dígitos, y
// un RUT sin dv (8) o con el dv pegado (9) es exactamente eso: con eso un
// `paciente_id: String(rut)` entraba al disco del médico. En la versión de antes
// esos mismos valores los bloqueaba la heurística de dígitos, o sea el "arreglo"
// abría lo que venía cerrado.
//
// Y la rama de enteros no entregaba nada: los 16 call sites mandan los `*_id`
// como NÚMERO (`unidad_id: unidad.id`), que entra por la rama de escalares y ni
// siquiera llega acá. Del otro lado, `tab_id: "12345678"` tampoco está en la
// allowlist de ids opacos del backend, así que lo rechaza su propio filtro de
// RUT. O sea: su único efecto neto era escribir en el disco lo que el servidor
// iba a descartar. Vale el mismo argumento que ya había escrito para `gen_id`.
const esIdOpaco = (k) => k === "gen_id" || /_id$/.test(k);
const _UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

// Una corrida larga de dígitos es la barrera que de verdad trabaja. La regex del
// RUT del backend quedó fuera a propósito: medido, `_RUT` + `\d{7,}` descarta
// EXACTAMENTE lo mismo que `\d{7,}` sola (15,28% en los dos casos), y los
// formatos con punto que `_RUT` bloqueaba ya los rechaza el slug. Era
// documentación ejecutable, no una barrera — y una barrera inerte hace creer que
// hay dos.
const _CORRIDA_DIGITOS = /\d{7,}/;
const _VALOR_SLUG = /^[a-z0-9][a-z0-9_:-]{0,63}$/;

function metaSeguro(meta) {
  const out = {};
  try {
    for (const [k, v] of Object.entries(meta || {})) {
      if (typeof v === "number" || typeof v === "boolean") {
        out[k] = v;
      } else if (typeof v !== "string") {
        // nada que no sea escalar
      } else if (esIdOpaco(k)) {
        // Los ids opacos se validan por FORMA, no por heurística. Va PRIMERO,
        // que es donde estaba el bug.
        if (_UUID.test(v)) out[k] = v;
      } else if (!_CORRIDA_DIGITOS.test(v) && _VALOR_SLUG.test(v)) {
        out[k] = v;
      }
      // Todo lo demás se descarta: no se persiste ni se manda.
    }
  } catch (_) {}
  return out;
}

// ── Persistencia write-ahead, con el dueño en la clave ──────────────────────
// Mismo criterio que `turno-draft-buffer`: el dueño es el `authToken` CRUDO
// porque es síncrono (no depende de que `/api/user/me/` haya vuelto). Un buffer
// escrito por A es directamente ilegible con el token de B.
// Huella NO reversible del token de sesión.
//
// ⚠️ Por qué un hash y no el token, que es lo que hace `turno-draft-buffer`:
// porque acá el buffer SOBREVIVE al cierre de sesión a propósito (si se purgara,
// se perdería el `idle_logout` que se emite un instante antes, que es justo la
// métrica para la que existe `idle-telemetria`). Con el token textual eso dejaba
// una credencial viva en el disco de un computador de turno — el backend no
// revoca ni vence tokens. Con la huella no hay nada que robar: sirve para
// separar dueños y no sirve para entrar.
//
// Dos pasadas (djb2 + sdbm) para que no colisionen dos tokens distintos.
function _huella(txt) {
  let h1 = 5381;
  let h2 = 0;
  for (let i = 0; i < txt.length; i += 1) {
    const c = txt.charCodeAt(i);
    h1 = ((h1 << 5) + h1 + c) >>> 0;
    h2 = (c + (h2 << 6) + (h2 << 16) - h2) >>> 0;
  }
  return h1.toString(16).padStart(8, "0") + h2.toString(16).padStart(8, "0");
}

function ownerToken() {
  try {
    const t = localStorage.getItem("authToken");
    return t ? _huella(t) : "sin-sesion";
  } catch (_) {
    return "sin-sesion";
  }
}

// La clave lleva el dueño Y la pestaña. El dueño impide el cruce entre médicos;
// la pestaña impide que dos pestañas del mismo médico se pisen el buffer — lo
// midió el revisor: con una sola clave, cada `persistir()` pisaba el arreglo
// entero y un evento de la otra pestaña desaparecía del disco, que es
// exactamente el caso que la persistencia venía a cubrir.
function claveBuffer(owner, sesion) {
  return (
    PREFIX +
    encodeURIComponent(owner || ownerToken()) +
    ":" +
    encodeURIComponent(sesion || SESSION_ID)
  );
}

function _clavesDelBuffer() {
  const out = [];
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PREFIX)) out.push(k);
    }
  } catch (_) {}
  return out;
}

function _dueñoDeClave(clave) {
  try {
    const resto = clave.slice(PREFIX.length);
    return decodeURIComponent(resto.split(":")[0] || "");
  } catch (_) {
    return "";
  }
}

// (Acá vivía `purgarActivityQueue()`. Se borró: quedó sin un solo llamador de
// producción cuando el dueño pasó a ser una huella y `logout()` dejó de purgar,
// y tenía un test propio que la certificaba. Un test verde sobre código que
// nadie ejecuta es de los que dan permiso. Si algún día hace falta purgar, el
// barrido de abajo ya sabe hacerlo.)

// Barre lo que quedó de OTROS dueños o de sesiones vencidas. Sin esto las claves
// ajenas no las borraba nadie nunca: el TTL solo se miraba para el dueño actual.
function _barrerAjenasYVencidas(owner) {
  for (const k of _clavesDelBuffer()) {
    try {
      if (_dueñoDeClave(k) !== owner) {
        localStorage.removeItem(k);
        continue;
      }
      const dato = JSON.parse(localStorage.getItem(k) || "{}");
      if (Date.now() - Number(dato?.ts || 0) > MAX_AGE_MS) {
        localStorage.removeItem(k);
      }
    } catch (_) {
      try {
        localStorage.removeItem(k);
      } catch (_) {}
    }
  }
}

// Persiste la cola bajo la clave del dueño ACTUAL. Mientras no hay sesión no se
// escribe nada: una clave neutra dejaría esos eventos disponibles para que los
// adopte el próximo que entre, que es justo el cruce de identidad que la clave
// con dueño viene a impedir. Apenas hay sesión, la cola entera —incluidos los
// que se produjeron antes del login, en esta misma carga— se escribe bajo ese
// dueño.
// Tira los eventos más viejos que el TTL, uno por uno.
//
// El TTL de 24 h era del SOBRE y se refrescaba en cada `persistir()`, así que un
// evento individual vivía indefinidamente mientras el médico siguiera
// trabajando. Eso importa por dos motivos: el dedupe del backend solo mira 2
// días atrás, así que un evento retenido más tiempo se cuenta DOS veces; y una
// fecha vieja distorsiona el conteo por día del vigilante. Ahora que cada evento
// lleva su `ts`, la poda es por evento y cierra las dos cosas con el mismo campo.
function podarViejos(lista) {
  const corte = Date.now() - MAX_AGE_MS;
  const vivos = lista.filter((e) => {
    const ts = e?.meta?.ts;
    return typeof ts !== "number" || ts >= corte;
  });
  // Lo que se poda se CUENTA, y viaja en el `activity_dropped` que ya existe.
  // La primera versión borraba en silencio, contra la doctrina escrita cuatro
  // párrafos más arriba en este mismo archivo ("un descarte contado es un dato,
  // uno mudo es exactamente el bug que este archivo vino a arreglar"). Y el caso
  // no es raro: un PC con el reloj atrasado que después se sincroniza manda al
  // pasado todo lo encolado y lo hacía desaparecer de una.
  const podados = lista.length - vivos.length;
  if (podados > 0) descartadosAcumulados += podados;
  return vivos;
}

// Deja UN marcador acumulativo con todo lo descartado desde la última entrega.
// Lo usan los DOS caminos que descartan —el tope de buffer y la poda por edad—
// porque si no, uno de los dos borra en silencio. El marcador se rehace en vez
// de editarse para que quede al final: el descarte tira desde el frente, así que
// un marcador al principio era lo primero en caerse.
function asegurarMarcador() {
  if (descartadosAcumulados <= 0) return;
  queue = queue.filter((e) => e.event_type !== "activity_dropped");
  seqCounter += 1;
  queue.push({
    _owner: ownerToken(),
    event_type: "activity_dropped",
    meta: { count: descartadosAcumulados, seq: seqCounter, ts: Date.now() },
    session_id: SESSION_ID,
  });
}

function persistir() {
  try {
    const owner = ownerToken();
    if (owner === "sin-sesion") return;
    queue = podarViejos(queue);
    asegurarMarcador();
    if (!queue.length) {
      localStorage.removeItem(claveBuffer(owner));
      return;
    }
    localStorage.setItem(
      claveBuffer(owner),
      JSON.stringify({ ts: Date.now(), eventos: queue })
    );
  } catch (_) {
    // localStorage lleno o Safari en privado: la cola sigue viva en memoria.
  }
}

// Rehidrata lo que quedó sin enviar en cargas anteriores. Solo del dueño ACTUAL,
// y de TODAS sus pestañas (cada una tiene su clave, ver `claveBuffer`).
function rehidratar() {
  try {
    const owner = ownerToken();
    if (owner === "sin-sesion") return;
    _barrerAjenasYVencidas(owner);
    const recuperados = [];
    const adoptadas = [];
    for (const k of _clavesDelBuffer()) {
      if (_dueñoDeClave(k) !== owner) continue;
      if (k === claveBuffer(owner)) continue; // la de ESTA pestaña, aún vacía
      try {
        const dato = JSON.parse(localStorage.getItem(k) || "{}");
        const eventos = Array.isArray(dato?.eventos) ? dato.eventos : [];
        if (eventos.length) recuperados.push(...eventos);
        adoptadas.push(k);
      } catch (_) {
        try {
          localStorage.removeItem(k); // corrupta: esa sí se tira
        } catch (_) {}
      }
    }
    if (recuperados.length) {
      // Al frente: lo viejo sale primero.
      queue = podarViejos(recuperados).concat(queue).slice(-MAX_BUFFER);
      // Se escribe ANTES de borrar las adoptadas: con `localStorage` lleno,
      // `persistir()` se traga el error de cuota y el read-delete-write perdía
      // el backlog entero.
      persistir();
      const quedo = (() => {
        try {
          return Boolean(localStorage.getItem(claveBuffer(owner)));
        } catch (_) {
          return false;
        }
      })();
      if (quedo) {
        for (const k of adoptadas) {
          try {
            localStorage.removeItem(k);
          } catch (_) {}
        }
      }
    }
  } catch (_) {
    /* un buffer corrupto no puede impedir que la app arranque */
  }
}

/**
 * Mueve el buffer de un `authToken` viejo a uno nuevo. SOLO se llama cuando el
 * backend rota el token del MISMO usuario ("Cambiar clave", "Cerrar las otras
 * sesiones"): sin esto, su propia telemetría sin enviar queda huérfana bajo la
 * clave vieja. No reabre el cruce: mueve una sola clave, la del dueño viejo.
 */
export function rekeyActivityQueue(tokenViejo, tokenNuevo) {
  try {
    if (!tokenViejo || !tokenNuevo || tokenViejo === tokenNuevo) return;
    const viejo = _huella(tokenViejo);
    const nuevo = _huella(tokenNuevo);
    // El dueño va en la clave Y adentro de cada evento. La primera versión movía
    // solo la clave, así que el evento quedaba huérfano por dueño en vez de por
    // clave: nunca salía, nunca vencía, se re-persistía en cada escritura y se
    // adoptaba en cada carga. Inmortal y mudo. Y es alcanzable sin recarga con
    // "Cerrar las otras sesiones" y "Cambiar clave".
    for (const e of queue) {
      if (e._owner === viejo) e._owner = nuevo;
    }
    for (const k of _clavesDelBuffer()) {
      if (_dueñoDeClave(k) !== viejo) continue;
      const sesion = decodeURIComponent(k.slice(PREFIX.length).split(":")[1] || "");
      const crudo = localStorage.getItem(k);
      if (!crudo) continue;
      let dato;
      try {
        dato = JSON.parse(crudo);
      } catch (_) {
        localStorage.removeItem(k);
        continue;
      }
      for (const e of dato.eventos || []) {
        if (e._owner === viejo) e._owner = nuevo;
      }
      localStorage.setItem(claveBuffer(nuevo, sesion), JSON.stringify(dato));
      localStorage.removeItem(k);
    }
  } catch (_) {}
}

// ── Envío ────────────────────────────────────────────────────────────────────
// Un batch (<= MAX_BATCH). Devuelve true SOLO si el servidor confirmó con 2xx.
//
// `keepalive` porque el flush también corre en `pagehide`; `sendBeacon` no sirve
// porque no permite el header Authorization.
//
// El camino: `urlApi` ya devuelve la ruta relativa si la sesión comprobó que
// esta red necesita el proxy. Si estamos yendo directo y falla la RED (no un
// status: un fallo de red), se reintenta UNA vez same-origin — y si eso anda, se
// recuerda, igual que hace `get-client`. Es la mitad que faltaba: sin esto, en
// una red que bloquea el backend la telemetría no tiene forma de salir nunca.
async function sendBatch(events, token) {
  // `_owner` es estado interno (lleva el token): se saca antes de salir a la red.
  const limpios = events.map(({ _owner, ...resto }) => resto);
  // ⚠️ Timeout duro. Sin esto, UN `fetch` colgado mataba la telemetría de toda
  // la carga de página: `enVuelo` quedaba en true para siempre y con él el
  // flush, el reintento y el timer. Y no es hipotético — es EL modo de falla del
  // firewall de hospital que este módulo vino a cubrir: `get-client.js` lo dice
  // ("un firewall que descarta en silencio hace esperar el timeout completo") y
  // por eso su sonda usa el mismo criterio. Además, sin rechazo no hay fallback
  // same-origin: contra un firewall que traga paquetes no hay `catch`, hay
  // cuelgue.
  const pedir = (url) => {
    let abortar = null;
    let ctrl = null;
    try {
      ctrl = new AbortController();
      abortar = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
    } catch (_) {
      ctrl = null;
    }
    return fetch(url, {
      method: "POST",
      keepalive: true,
      ...(ctrl ? { signal: ctrl.signal } : {}),
      headers: {
        Authorization: `Token ${token}`,
        "Content-Type": "application/json",
      },
      // `t_envio`: la hora del CLIENTE en el momento de mandar. El servidor
      // calcula `desfase = ahora_servidor - t_envio` y corrige cada `ts` con él.
      // Eso cancela cualquier corrimiento CONSTANTE del reloj del computador
      // —grande o chico— sin tocar la retención real, porque `t_envio - ts` es
      // una duración medida enteramente contra el mismo reloj y por lo tanto
      // inmune al offset. Reemplaza un umbral elegido a ojo por una resta.
      body: JSON.stringify({ events: limpios, t_envio: Date.now() }),
    }).finally(() => {
      if (abortar) {
        try {
          clearTimeout(abortar);
        } catch (_) {}
      }
    });
  };

  const directo = !proxyYaComprobado();
  try {
    const resp = await pedir(urlApi(ACTIVITY_PATH));
    return { ok: Boolean(resp && resp.ok), status: resp ? resp.status : 0 };
  } catch (err) {
    if (!directo) return { ok: false, status: 0 };
    try {
      const resp = await pedir(ACTIVITY_PATH); // same-origin, relativa
      if (resp && resp.ok) {
        recordarProxy(true);
        return { ok: true, status: resp.status };
      }
      return { ok: false, status: resp ? resp.status : 0 };
    } catch (_) {
      return { ok: false, status: 0 };
    }
  }
}

function programarReintento() {
  if (timerReintento) return;
  const espera = Math.min(
    BACKOFF_MAX_MS,
    BACKOFF_BASE_MS * Math.pow(2, Math.max(0, intentosFallidos - 1))
  );
  try {
    timerReintento = setTimeout(() => {
      timerReintento = null;
      flushActivity();
    }, espera);
  } catch (_) {
    timerReintento = null;
  }
}

/**
 * Intenta entregar la cola. La cola NO se vacía hasta que el servidor confirma.
 *
 * Nunca lanza. Nunca bloquea. Si no hay token, RETIENE (no descarta): el caso
 * más común es la ventana entre que se vence la sesión y el médico vuelve a
 * entrar, y ahí antes se perdía todo lo acumulado.
 */
export function flushActivity() {
  try {
    if (!queue.length) return;
    if (enVuelo) {
      // Cinturón sobre el `AbortController`: si el envío lleva colgado más del
      // doble del timeout, se lo da por perdido y se suelta la bandera. Sin
      // esto, un `fetch` que nunca resuelve ni rechaza —y hay entornos donde la
      // señal de aborto no se honra— dejaba tomados el flush, el reintento y el
      // timer para TODA la carga de página. La telemetría no puede depender de
      // que la red se porte bien: es lo único que la va a contar cuando no lo
      // haga.
      if (Date.now() - enVueloDesde < TIMEOUT_MS * 2) return;
      // El lote abandonado se trata como uno fallido: si no resolvió NI
      // rechazó, nadie restituía lo que se le había descontado al marcador, y
      // los descartes que llevaba quedaban sin reportar. No se pierde ningún
      // evento —siguen en la cola— pero el número que dice cuántos se tiraron
      // quedaba corto, que es la mitad del trabajo de este contador.
      //
      // ⚠️ SIN COBERTURA POR MUTACIÓN, y queda dicho a propósito: revertir estas
      // tres líneas no pone roja ninguna prueba. No es falta de tests, es que el
      // efecto no es observable desde la superficie pública — para llegar acá
      // hace falta un envío que no resuelva NI rechace (o sea un entorno que
      // ignore el AbortController) Y que el marcador viaje en ese lote. Escribí
      // un test, vi que pasaba con y sin el arreglo, y lo borré: un test que no
      // puede fallar es peor que no tenerlo, porque da permiso. Se queda el
      // arreglo, que es el lado defensivo, y se queda esta nota.
      descartadosAcumulados += descartadosEnVuelo;
      descartadosEnVuelo = 0;
      loteEnVuelo = new Set();
      enVuelo = false;
    }
    const token = getAuthToken();
    if (!token) {
      // Sin sesión no se puede atribuir el evento a nadie. Se retiene.
      // Cuenta como intento fallido a propósito: si no, `programarReintento`
      // calculaba siempre 2 s y quedaba un despertador fijo en la pantalla de
      // login, donde `App.js` emite `module_open` sin sesión.
      intentosFallidos += 1;
      programarReintento();
      return;
    }
    enVuelo = true;
    // ⚠️ Esta línea es el candado. Sin ella, la guarda de arriba compara contra
    // 0, la resta da ~1.8e12, la condición nunca se cumple y `enVuelo` se suelta
    // SIEMPRE: dos flushes mandan el mismo lote. Medido por el revisor: 101 POST
    // para 150 eventos, todos con el mismo lote de 50 y con `keepalive`, contra
    // la red que justamente anda mal. Y la suite pasaba 28/28 con el candado
    // muerto Y con el candado vivo, porque ninguna prueba miraba la
    // concurrencia. Ahora hay una.
    enVueloDesde = Date.now();
    // Solo lo de ESTE dueño. Lo de otro se queda: o lo rescata su dueño al
    // volver, o lo barre `purgarActivityQueue`/el TTL. Jamás sale firmado por
    // quien no lo produjo.
    // `sin-sesion` son los que se produjeron antes del login (ej. el
    // `module_open` de la pantalla de entrada). Los adopta el token de ESTA
    // carga de página, que es la misma persona: en una pantalla de login solo
    // está quien va a entrar.
    // ⚠️ Ojo con la razón: NO es que "nunca se persisten" (eso decía la versión
    // anterior y es falso — en cuanto aparece un token, `persistir()` los
    // escribe bajo la clave del dueño nuevo). Es que en el momento en que se
    // produjeron no había sesión que los reclamara.
    const duenio = ownerToken();
    const propios = queue.filter(
      (e) => !e._owner || e._owner === duenio || e._owner === "sin-sesion"
    );
    if (!propios.length) {
      enVuelo = false;
      return;
    }
    const lote = propios.slice(0, MAX_BATCH);
    loteEnVuelo = new Set(lote);
    const marcadorEnLote = lote.find((e) => e.event_type === "activity_dropped");
    if (marcadorEnLote) {
      descartadosEnVuelo = marcadorEnLote.meta.count || 0;
      descartadosAcumulados = Math.max(0, descartadosAcumulados - descartadosEnVuelo);
    }
    sendBatch(lote, token)
      .then(({ ok }) => {
        if (ok) {
          const entregados = new Set(lote);
          descartadosEnVuelo = 0; // ya se descontó al armar el lote
          loteEnVuelo = new Set();
          queue = queue.filter((e) => !entregados.has(e));
          intentosFallidos = 0;
          persistir();
          enVuelo = false;
          if (queue.length) flushActivity();
          return;
        }
        // 401/403/5xx/red: se retiene y se reintenta con backoff. Da igual el
        // status: en todos los casos el evento NO llegó, que es lo único que
        // hay que saber para no perderlo.
        // No llegó: el marcador vuelve al acumulado.
        descartadosAcumulados += descartadosEnVuelo;
        descartadosEnVuelo = 0;
        loteEnVuelo = new Set();
        intentosFallidos += 1;
        enVuelo = false;
        programarReintento();
      })
      .catch(() => {
        descartadosAcumulados += descartadosEnVuelo;
        descartadosEnVuelo = 0;
        loteEnVuelo = new Set();
        intentosFallidos += 1;
        enVuelo = false;
        programarReintento();
      });
  } catch (err) {
    enVuelo = false;
    try {
      console.debug("activity-log flushActivity threw", err);
    } catch (_) {}
  }
}

// Cablea (una sola vez) el timer periódico y los listeners de cierre/ocultado.
function ensureWired() {
  if (listenersWired) return;
  listenersWired = true;
  rehidratar();
  try {
    if (typeof window !== "undefined") {
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") flushActivity();
      });
      window.addEventListener("pagehide", () => flushActivity());
    }
  } catch (_) {}
  try {
    setInterval(() => {
      // ⚠️ `timerReintento` es la condición de guarda, no un detalle: sin esto
      // el flush periódico PISA el backoff y el reintento queda decorativo. El
      // revisor lo midió: con el servidor en 500 salían 39 POST en 5 minutos en
      // vez de ~8, indefinidamente, y una pestaña con la sesión muerta
      // martillaba el endpoint cada 10 s hasta que alguien la cerrara.
      // La guarda es el ESTADO (`intentosFallidos`), no solo el temporizador:
      // entre que el reintento dispara y el `.then` agenda el siguiente hay una
      // ventana con `timerReintento` en null, y el interval se colaba justo ahí
      // (medido: 7 POST en 60 s en vez de 5). Mientras haya fallas, el ritmo lo
      // manda el backoff y nadie más.
      // No pisar el backoff, pero tampoco vetar la recuperación.
      //
      // Vetar por `intentosFallidos > 0` abría una tumba: si un reintento se
      // colgaba, el `.then` no corría, nadie agendaba el siguiente, y el interval
      // quedaba prohibido para siempre — cinco minutos con la red sana y cero
      // intentos. Los dos arreglos (el del backoff y el del cuelgue) se pisaban.
      //
      // La condición correcta es "hay algo en curso": un reintento agendado, o un
      // envío en vuelo DENTRO de su ventana.
      const enCurso =
        Boolean(timerReintento) ||
        (enVuelo && Date.now() - enVueloDesde < TIMEOUT_MS * 2);
      if (queue.length && !enCurso) flushActivity();
    }, FLUSH_INTERVAL_MS);
  } catch (_) {}
}

// La telemetría nunca lanza: si leer el modo de pantalla falla, el evento sale con false.
function standaloneSeguro() {
  try {
    return isStandalone() === true;
  } catch {
    return false;
  }
}

/**
 * Encola un evento de telemetría.
 *
 * @param {string} event_type  Enum del evento (ej. "module_open", "turno_generate").
 * @param {object} [opts]
 * @param {string} [opts.module]  Nombre de la vista/módulo (enum, ej. "turno").
 * @param {object} [opts.meta]    SOLO ids/counts/bools/enums — NUNCA PHI.
 *
 * Nunca lanza: si algo falla, se traga en silencio.
 */
export function logActivity(event_type, { module, meta } = {}) {
  try {
    if (!event_type) return;
    ensureWired();
    seqCounter += 1;
    queue.push({
      // De quién es este evento. La clave del disco ya impedía el cruce entre
      // médicos; en MEMORIA no había nada, y la ventana entre que
      // `establecerSesion` escribe el token nuevo y la recarga que le sigue
      // alcanza para que el timer mande el evento de A firmado por B. La
      // protección no puede depender de una invariante no escrita
      // ("todo relevo recarga la página").
      _owner: ownerToken(),
      event_type,
      module: module || undefined,
      // `seq` va SIEMPRE (aunque no haya meta): ordena los eventos de un mismo
      // batch en el timeline, y junto a `session_id` es la llave con la que el
      // backend deduplica — que es lo que hace segura la entrega al-menos-una-vez.
      // `ts`: cuándo PASÓ, no cuándo se entregó. Desde que la cola sobrevive y
      // reintenta, la hora de llegada al servidor puede estar corrida hasta 24 h
      // — y la unidad de análisis de `auditar_telemetria` es (día, usuario). Sin
      // esto, el propio rescate que agrega este módulo puede fabricar un día
      // ciego e inflar otro, y el vigilante no podría distinguirlo: el
      // instrumento mintiendo otra vez, que es de lo que se trata todo esto.
      // `standalone`: si la app corría instalada en el inicio cuando pasó el evento. Va junto a
      // `seq`/`ts` (después de `metaSeguro`) para que ningún call site lo pise. Es un booleano:
      // el backend lo guarda solo si es exactamente true/false (`finetuning/activity.py`
      // `_STRICT_META_KEYS`) y la checklist de uso lo lee para "app en el inicio".
      // Cubre R24 — .claude/specs/1686-checklist-uso-celular/
      meta: { ...metaSeguro(meta), seq: seqCounter, ts: Date.now(), standalone: standaloneSeguro() },
      session_id: SESSION_ID,
    });

    // Tope del buffer: se tira lo MÁS VIEJO y queda constancia del descarte.
    //
    // UN marcador acumulativo, no uno por evento: la primera versión dejaba 200
    // marcadores y solo 301 eventos reales de 500, porque cada evento nuevo
    // descartaba dos. Y el conteo va en `count`, NO en `n_chars`: esa key
    // significa "largo del texto escrito" y `timeline.py` la pinta como
    // "N car.", así que el timeline del médico mostraba 200 líneas de "2 car.".
    if (queue.length > MAX_BUFFER) {
      // Se tira lo MÁS VIEJO que NO esté en vuelo: un evento que ya salió a la
      // red se va a entregar, y descartarlo acá contaría como pérdida algo que
      // no se perdió.
      let porTirar = queue.length - MAX_BUFFER;
      const quedan = [];
      let tirados = 0;
      for (const e of queue) {
        if (porTirar > 0 && !loteEnVuelo.has(e) && e.event_type !== "activity_dropped") {
          porTirar -= 1;
          tirados += 1;
          continue;
        }
        quedan.push(e);
      }
      queue = quedan;
      if (tirados > 0) {
        descartadosAcumulados += tirados;
        asegurarMarcador();
      }
    }

    persistir();
    if (queue.length >= MAX_BATCH) flushActivity();
  } catch (err) {
    try {
      console.debug("activity-log logActivity threw", err);
    } catch (_) {}
  }
}

/** Solo para tests: limpia el estado de módulo (la cola vive acá adentro). */
export function resetActivityLog() {
  queue = [];
  seqCounter = 0;
  enVuelo = false;
  enVueloDesde = 0;
  intentosFallidos = 0;
  descartadosAcumulados = 0;
  descartadosEnVuelo = 0;
  loteEnVuelo = new Set();
  if (timerReintento) {
    try {
      clearTimeout(timerReintento);
    } catch (_) {}
  }
  timerReintento = null;
  listenersWired = false;
}

export const __testing = { claveBuffer, ownerToken, PREFIX, MAX_BUFFER, MAX_AGE_MS };

export default logActivity;
